import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable, KanbanBoard, LeadCard } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { EmptyState } from '@ds/feedback';
import { SearchField, Select } from '@ds/forms';
import { SegmentedControl } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { moeda, proximoContato } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { CORES_DA_ETAPA, TOM_DA_SITUACAO, proximaEtapa } from '@/data/funis';
import { dados } from '@/data/porta';
import type { CargaDoPipeline, EtapaDoFunil, Lead } from '@/data/tipos';
import { useRotaAtual } from '@/router/useRotaAtual';
import { pode } from '@/session/sessao';
import { DetalheDoLead } from './contatos/DetalheDoLead';
import { EditorDeEtapas } from './crm/EditorDeEtapas';
import { NovoPipelineModal } from './crm/NovoPipelineModal';

export async function carregarCrm({ request }: LoaderFunctionArgs): Promise<CargaDoPipeline> {
    const url = new URL(request.url);
    return dados().pipeline({
        busca: url.searchParams.get('q') ?? undefined,
        responsavel: url.searchParams.get('responsavel') ?? undefined,
        funilId: url.searchParams.get('funil') ?? undefined,
    });
}

export function Crm() {
    const pipeline = useLoaderData() as CargaDoPipeline;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const handle = useRotaAtual();
    /* "Ver no funil" e "Ver oportunidade" chegam com o lead na URL. Antes
       caíam no quadro e a pessoa tinha de procurar de novo o que já tinha
       aberto. */
    const [aberto, setAberto] = useState<{ lead: Lead; editando: boolean } | null>(() => {
        const idDaUrl = params.get('lead');
        const lead = idDaUrl ? pipeline.leads.find((l) => l.id === idDaUrl) : undefined;
        return lead ? { lead, editando: false } : null;
    });
    const [criandoPipeline, setCriandoPipeline] = useState(false);

    const visao = handle?.subrota?.path ?? 'pipeline';
    const busca = params.get('q') ?? '';
    const filtrado = busca !== '' || Boolean(params.get('responsavel'));
    /* A estrutura do funil é da gestão da clínica (0100): quem não pode editar vê
       as etapas, e não os controles. O banco recusaria de qualquer jeito. */
    const podeEditarFunil = pode('configuracoes');

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            return proximos;
        }, { replace: true });
    };

    const mover = async (lead: Lead, etapa: EtapaDoFunil) => {
        try {
            await dados().moverLeadDeEtapa(lead.id, etapa.id);
            aviso({ title: 'Lead movido', description: `${lead.nome} · ${etapa.titulo}` });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível mover', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const colunasDaLista: DataTableColumn<Lead>[] = [
        { key: 'nome', header: 'Lead', sortable: true },
        { key: 'etapa', header: 'Etapa', render: (l) => <Badge tone={TOM_DA_SITUACAO[l.situacao]} dot>{l.etapa}</Badge> },
        { key: 'origem', header: 'Origem' },
        { key: 'interesse', header: 'Interesse' },
        { key: 'responsavel', header: 'Responsável' },
        { key: 'proximoContatoEm', header: 'Próximo contato', render: (l) => proximoContato(l.proximoContatoEm) },
        { key: 'valorEstimadoCentavos', header: 'Valor', align: 'right', render: (l) => moeda(l.valorEstimadoCentavos) },
    ];

    const leadsPorEtapa = pipeline.leads.reduce<Record<string, number>>((acc, l) => {
        acc[l.etapaId] = (acc[l.etapaId] ?? 0) + 1;
        return acc;
    }, {});
    /* O editor remonta quando o funil muda no servidor: etapa nova volta com id, e
       o rascunho local deixaria de bater com o que foi salvo. */
    const versaoDoFunil = [
        pipeline.funil.id, pipeline.funil.nome,
        ...pipeline.funil.etapas.map((e) => `${e.id}:${e.titulo}:${e.ordem}:${e.cor}:${e.arquivada ? 'a' : ''}`),
    ].join('|');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%', minHeight: 0 }}>
            <PageHeader
                title="CRM"
                /* Os dois números são diferentes de propósito: a lista mostra
                   tudo, inclusive perdido, e o valor "em jogo" conta só o que
                   ainda pode virar venda. Dizer isso evita a leitura errada de
                   que sumiu registro. */
                description={
                    `${pipeline.leads.length} ${pipeline.leads.length === 1 ? 'oportunidade' : 'oportunidades'}`
                    + ` · ${pipeline.colunas.reduce((s, c) => s + c.leads.length, 0)} no funil`
                    + ` · ${moeda(pipeline.totalCentavos)} em jogo`
                }
                actions={
                    <>
                        {podeEditarFunil && (
                            <Button variant="secondary" iconLeft={<Icon name="git-branch-plus" size={15} />} onClick={() => setCriandoPipeline(true)}>
                                Novo pipeline
                            </Button>
                        )}
                        <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => navigate('/app/contatos/leads')}>Novo lead</Button>
                    </>
                }
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {/* Com um pipeline só, o seletor não teria o que escolher. */}
                    {pipeline.funis.length > 1 && (
                        <Select
                            value={pipeline.funil.id} onChange={(v) => trocarParam('funil', v)} size="sm" style={{ width: 190 }}
                            aria-label="Pipeline"
                            options={pipeline.funis.map((f) => ({ value: f.id, label: f.nome }))}
                        />
                    )}
                    <SearchField value={busca} onChange={(v) => trocarParam('q', v || null)} placeholder="Buscar por nome ou interesse" width={260} />
                    <Select
                        value={params.get('responsavel') ?? ''} onChange={(v) => trocarParam('responsavel', v || null)} size="sm" style={{ width: 200 }}
                        aria-label="Filtrar por responsável"
                        options={[{ value: '', label: 'Todos os responsáveis' }, ...pipeline.responsaveis.map((r) => ({ value: r, label: r }))]}
                    />
                    <span style={{ marginLeft: 'auto' }} />
                    <SegmentedControl
                        value={visao} ariaLabel="Visão do funil"
                        onChange={(v) => navigate({ pathname: `/app/crm/${v}`, search: params.toString() ? `?${params}` : '' })}
                        options={[
                            { value: 'pipeline', label: 'Pipeline' },
                            { value: 'lista', label: 'Lista' },
                            { value: 'etapas', label: 'Etapas' },
                        ]}
                    />
                </div>
            </PageHeader>

            {visao === 'pipeline' && (
                <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                    {pipeline.colunas.map((c) => (
                        <KpiCard key={c.id} label={c.titulo} value={String(c.leads.length)} hint={moeda(c.totalCentavos)} />
                    ))}
                </div>
            )}

            {visao === 'pipeline' && (
                <KanbanBoard
                    style={{ flex: 1, minHeight: 0 }}
                    columns={pipeline.colunas.map((c) => ({
                        id: c.id,
                        title: c.titulo,
                        /* A cor é a que a clínica escolheu para a etapa: quem olha o
                           quadro de longe lê o avanço pela faixa. */
                        color: CORES_DA_ETAPA[c.cor],
                        total: moeda(c.totalCentavos),
                        items: c.leads.map((l) => ({ ...l, id: l.id })),
                    }))}
                    renderCard={(lead, coluna) => {
                        const proxima = proximaEtapa(pipeline.funil, coluna.id);
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <LeadCard
                                    name={lead.nome} value={moeda(lead.valorEstimadoCentavos)} source={lead.origem}
                                    owner={lead.responsavel} tag={lead.interesse}
                                    dueLabel={proximoContato(lead.proximoContatoEm)}
                                    overdue={proximoContato(lead.proximoContatoEm).startsWith('Atrasado')}
                                    onClick={() => setAberto({ lead, editando: false })}
                                />
                                {/* Avançar por botão, não só por arrastar: arrastar não
                                    existe para quem usa teclado, e a coluna seguinte é
                                    a ação que a pessoa faz o dia inteiro. */}
                                {proxima && (
                                    <Button size="sm" variant="ghost" onClick={() => void mover(lead, proxima)}>
                                        Avançar etapa
                                    </Button>
                                )}
                            </div>
                        );
                    }}
                />
            )}

            {visao === 'lista' && (
                <DataTable
                    columns={colunasDaLista} rows={pipeline.leads} rowKey="id"
                    onRowClick={(l) => setAberto({ lead: l, editando: false })}
                    rowLabel={(l) => `${l.nome} · ${l.etapa}`}
                    emptyMessage="Nenhuma oportunidade neste pipeline."
                />
            )}

            {visao === 'etapas' && (
                <EditorDeEtapas
                    key={versaoDoFunil}
                    funil={pipeline.funil}
                    leadsPorEtapa={leadsPorEtapa}
                    filtrado={filtrado}
                    podeEditar={podeEditarFunil}
                    onMudou={() => revalidator.revalidate()}
                />
            )}

            {visao === 'pipeline' && pipeline.leads.length === 0 && (
                <Card padding={0}>
                    <EmptyState icon="target" title="Nenhuma oportunidade" description="Leads criados em Contatos aparecem aqui." />
                </Card>
            )}

            <DetalheDoLead
                lead={aberto?.lead ?? null} editando={aberto?.editando}
                onClose={() => { setAberto(null); if (params.get('lead')) trocarParam('lead', null); }}
                onSalvo={() => revalidator.revalidate()}
                onVerNoFunil={() => { setAberto(null); if (params.get('lead')) trocarParam('lead', null); }}
            />

            <NovoPipelineModal
                open={criandoPipeline}
                onClose={() => setCriandoPipeline(false)}
                onCriado={(funil) => navigate({ pathname: '/app/crm/pipeline', search: `?funil=${encodeURIComponent(funil.id)}` })}
            />
        </div>
    );
}
